<template>
  <div class="card-form">
    <PageMain
      ref="main"
      :tableData="tableData"
      :loading="loading"
      :tablePage="page"
      @reload="reload"
      @checkbox-change="(rows) => (checkedRows = rows)"
    >
      <template #header>
        <PageHeader ref="header" @query="getData" @reset="resetPage">
          <b-button
            v-if="allow('A')"
            size="sm"
            class="ml-2"
            variant="outline-success"
            @click="chooseUserVisible = true"
          >
            {{ $l('btn_add_inspector') || '增加验货人员' }}
          </b-button>

          <el-badge
            v-if="allow('D')"
            :value="checkedRowCount"
            :hidden="!checkedRowCount"
            class="clearable-button"
            :class="{ clearable: !!checkedRowCount }"
          >
            <b-button
              size="sm"
              class="ml-2"
              variant="outline-danger"
              :disabled="!checkedRowCount"
              @click="deleteInspectors()"
            >
              {{ $l('btn_del_inspector') || '删除验货人员' }}

              <i class="el-icon-circle-close" @click.stop="clearCheckboxRow" />
            </b-button>
          </el-badge>

          <el-badge
            v-if="allow('SM')"
            :value="checkedRowCount"
            :hidden="!checkedRowCount"
            class="clearable-button"
            :class="{ clearable: !!checkedRowCount }"
          >
            <b-button
              size="sm"
              class="ml-2"
              variant="outline-primary"
              :disabled="!checkedRowCount"
              @click="setManager()"
            >
              {{ $l('btn_set_manager') || '设置验货主管' }}

              <i class="el-icon-circle-close" @click.stop="clearCheckboxRow" />
            </b-button>
          </el-badge>

          <el-badge
            v-if="allow('SI')"
            :value="checkedRowCount"
            :hidden="!checkedRowCount"
            class="clearable-button"
            :class="{ clearable: !!checkedRowCount }"
          >
            <b-button
              size="sm"
              class="ml-2"
              variant="outline-primary"
              :disabled="!checkedRowCount"
              @click="setInspector()"
            >
              {{ $l('btn_set_inspector') || '设置验货人员' }}

              <i class="el-icon-circle-close" @click.stop="clearCheckboxRow" />
            </b-button>
          </el-badge>
        </PageHeader>
      </template>
      <template #pager>
        <pagination
          :current-page="page.curPage"
          :page-size="page.pageSize"
          :page-total="page.pageTotal"
          @change="handlePaginationChange"
        />
      </template>
    </PageMain>

    <ChooseUser :visible.sync="chooseUserVisible" @confirm="addInspectors" />
  </div>
</template>

<script>
import lodash from 'lodash'
import BaseTable from '@/mixins/base-table'
import EditLoading from '@/mixins/edit-loading'
import PageHeader from './header.vue'
import PageMain from './main.vue'
import ChooseUser from '../choose-user'

export default {
  components: { ChooseUser, PageHeader, PageMain },
  mixins: [BaseTable, EditLoading],
  data() {
    return {
      usePage: true,
      checkedRows: [],
      chooseUserVisible: false,
    }
  },
  computed: {
    checkedRowCount() {
      return this.checkedRows?.length || 0
    },
    listInspectorCode() {
      return lodash.map(this.checkedRows, 'inspectorCode')
    },
  },
  methods: {
    getData(form) {
      this.loading = true
      this.$api
        .dfInspectorListApi({ ...form, ...this.page })
        .then((res) => {
          this.loading = false
          this.setData(res)
        })
        .catch(() => {
          this.loading = false
        })
    },
    clearCheckboxRow() {
      this.checkedRows = []
      this.$refs.main.clearCheckboxRow()
    },
    async addInspectors(rows) {
      if (!rows?.length) return

      const listUserCode = rows.map((item) => item.userCode).filter(Boolean)
      if (!listUserCode.length) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .dfInspectorAddApi({ inspectorRole: 'I', listUserCode })
        .catch(() => null)
      this.loadingClose()

      if (!res) return

      const { status, msg } = res
      const error = status !== 200
      this.$message({
        showClose: true,
        message:
          msg ||
          (error
            ? this.$l('tips_add_failed') || '新增失败'
            : this.$l('tips_add_success') || '新增成功'),
        type: error ? 'warning' : 'success',
      })
      if (error) return

      this.clearCheckboxRow()
      this.reload()
    },
    async deleteInspectors() {
      if (!this.checkedRowCount) {
        this.$message.warning(
          this.$l('message_pl_select_delete_inspector') || '请选择需要删除的验货人员',
        )
        return
      }

      const confirm = await this.$confirm(
        this.$l('message_del_check_record') || '确认要删除选择的记录吗？',
        this.$l('INDEX.tips_title_confirm') || '确认信息',
        {
          type: 'warning',
          draggable: true,
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          confirmButtonText: this.$l('INDEX.btn_confirm') || '确认',
        },
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .dfInspectorDelApi({ listInspectorCode: this.listInspectorCode })
        .catch(() => null)
      this.loadingClose()

      if (!res) return

      const { status, msg } = res
      const error = status !== 200
      this.$message({
        showClose: true,
        message:
          msg ||
          (error
            ? this.$l('tips_delete_failed') || '删除失败'
            : this.$l('tips_delete_success') || '删除成功'),
        type: error ? 'error' : 'success',
      })

      if (error) return

      this.clearCheckboxRow()
      this.reload()
    },
    async setManager() {
      if (!this.checkedRowCount) {
        this.$message.warning(
          this.$l('message_pl_select_set_manager') || '请选择需要设置为验货主管的人员',
        )
        return
      }

      const confirm = await this.$confirm(
        this.$l('message_confirm_set_manager') || '确认要将选择的人员设置为验货主管吗？',
        this.$l('INDEX.tips_title_confirm') || '确认信息',
        {
          type: 'warning',
          draggable: true,
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          confirmButtonText: this.$l('INDEX.btn_confirm') || '确认',
        },
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .dfInspectorSetManagerApi({ listInspectorCode: this.listInspectorCode })
        .catch(() => null)
      this.loadingClose()

      if (!res) return

      const { status, msg } = res
      const error = status !== 200
      this.$message({
        showClose: true,
        message:
          msg ||
          (error
            ? this.$l('tips_set_manager_failed') || '设置验货主管失败'
            : this.$l('tips_set_manager_success') || '设置验货主管成功'),
        type: error ? 'error' : 'success',
      })

      if (error) return

      this.clearCheckboxRow()
      this.reload()
    },
    async setInspector() {
      if (!this.checkedRowCount) {
        this.$message.warning(
          this.$l('message_pl_select_set_inspector') || '请选择需要设置为验货人员的人员',
        )
        return
      }

      const confirm = await this.$confirm(
        this.$l('message_confirm_set_inspector') || '确认要将选择的人员设置为验货人员吗？',
        this.$l('INDEX.tips_title_confirm') || '确认信息',
        {
          type: 'warning',
          draggable: true,
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          confirmButtonText: this.$l('INDEX.btn_confirm') || '确认',
        },
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .dfInspectorSetInspectorApi({
          listInspectorCode: this.listInspectorCode,
        })
        .catch(() => null)
      this.loadingClose()

      if (!res) return

      const { status, msg } = res
      const error = status !== 200
      this.$message({
        showClose: true,
        message:
          msg ||
          (error
            ? this.$l('tips_set_inspector_failed') || '设置验货人员失败'
            : this.$l('tips_set_inspector_success') || '设置验货人员成功'),
        type: error ? 'error' : 'success',
      })

      if (error) return

      this.clearCheckboxRow()
      this.reload()
    },
  },
}
</script>

<style lang="scss" scoped>
.clearable-button {
  .el-icon-circle-close {
    display: none;
  }
}
.clearable-button.clearable:hover {
  .el-icon-circle-close {
    display: inline-block;
    z-index: 999;
  }
  .el-icon-circle-close:hover {
    transform: scale(1.2);
  }
}
</style>
